/**
 * Image Transform Panel - Crop, Rotate, Flip controls
 */

import * as React from 'react';
import { Button, cn } from '@frameit/ui';
import { useProjectStore, useSelectedArtwork, getLocalImageUrl } from '../../stores/projectStore';
import type { ImageTransform } from '@frameit/types';

export interface ImageTransformPanelProps {
  /** Custom class name */
  className?: string;
}

/** Aspect ratio presets for crop */
const ASPECT_PRESETS: { label: string; value: number | null }[] = [
  { label: 'Free', value: null },
  { label: '1:1', value: 1 },
  { label: '4:3', value: 4 / 3 },
  { label: '3:2', value: 3 / 2 },
  { label: '16:9', value: 16 / 9 },
  { label: '2:3', value: 2 / 3 },
];

/** Default image transform */
const DEFAULT_TRANSFORM: ImageTransform = {
  rotation: 0,
  flipH: false,
  flipV: false,
};

/** Image Transform Panel component */
export function ImageTransformPanel({ className }: ImageTransformPanelProps): React.JSX.Element {
  const selectedArtwork = useSelectedArtwork();
  const updateImageTransform = useProjectStore((s) => s.updateImageTransform);
  const saveToHistory = useProjectStore((s) => s.saveToHistory);

  const transform = selectedArtwork?.imageTransform ?? DEFAULT_TRANSFORM;

  // Crop state (local until applied)
  const [cropActive, setCropActive] = React.useState(false);
  const [cropRect, setCropRect] = React.useState<{
    x: number;
    y: number;
    width: number;
    height: number;
  }>({ x: 0, y: 0, width: 1, height: 1 });
  const [aspectRatio, setAspectRatio] = React.useState<number | null>(null);
  const [imageDimensions, setImageDimensions] = React.useState<{
    width: number;
    height: number;
  } | null>(null);

  // Drag state for crop handles
  const [dragHandle, setDragHandle] = React.useState<string | null>(null);
  const [dragStart, setDragStart] = React.useState<{
    x: number;
    y: number;
    rect: typeof cropRect;
  } | null>(null);
  const previewRef = React.useRef<HTMLDivElement>(null);

  // Load image for preview
  const imageUrl = selectedArtwork ? getLocalImageUrl(selectedArtwork.artworkId) : null;

  React.useEffect(() => {
    if (!imageUrl) return;
    const img = new Image();
    img.onload = () => setImageDimensions({ width: img.width, height: img.height });
    img.src = imageUrl;
  }, [imageUrl]);

  // Initialize crop rect when entering crop mode
  const startCrop = (): void => {
    setCropRect(transform.cropRect ?? { x: 0, y: 0, width: 1, height: 1 });
    setCropActive(true);
  };

  // Apply crop to store
  const applyCrop = (): void => {
    if (!selectedArtwork) return;
    saveToHistory();

    // If crop is the full image, remove cropRect
    const isFullImage =
      cropRect.x <= 0.001 &&
      cropRect.y <= 0.001 &&
      cropRect.width >= 0.999 &&
      cropRect.height >= 0.999;

    updateImageTransform(selectedArtwork.id, {
      cropRect: isFullImage ? undefined : cropRect,
    });
    setCropActive(false);
  };

  // Reset crop
  const resetCrop = (): void => {
    setCropRect({ x: 0, y: 0, width: 1, height: 1 });
  };

  // Handle rotation
  const handleRotate = (direction: 'cw' | 'ccw'): void => {
    if (!selectedArtwork) return;
    saveToHistory();
    const rotations: Array<0 | 90 | 180 | 270> = [0, 90, 180, 270];
    const currentIdx = rotations.indexOf(transform.rotation);
    const newIdx = direction === 'cw' ? (currentIdx + 1) % 4 : (currentIdx + 3) % 4;
    updateImageTransform(selectedArtwork.id, { rotation: rotations[newIdx] });
  };

  // Handle flip
  const handleFlipH = (): void => {
    if (!selectedArtwork) return;
    saveToHistory();
    updateImageTransform(selectedArtwork.id, { flipH: !transform.flipH });
  };

  const handleFlipV = (): void => {
    if (!selectedArtwork) return;
    saveToHistory();
    updateImageTransform(selectedArtwork.id, { flipV: !transform.flipV });
  };

  // Reset all transforms
  const handleResetAll = (): void => {
    if (!selectedArtwork) return;
    saveToHistory();
    updateImageTransform(selectedArtwork.id, {
      rotation: 0,
      flipH: false,
      flipV: false,
      cropRect: undefined,
    });
  };

  // Crop drag handlers
  const getRelativePosition = (clientX: number, clientY: number): { rx: number; ry: number } => {
    if (!previewRef.current) return { rx: 0, ry: 0 };
    const rect = previewRef.current.getBoundingClientRect();
    return {
      rx: Math.max(0, Math.min(1, (clientX - rect.left) / rect.width)),
      ry: Math.max(0, Math.min(1, (clientY - rect.top) / rect.height)),
    };
  };

  const handleCropPointerDown = (e: React.PointerEvent, handle: string): void => {
    e.preventDefault();
    e.stopPropagation();
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    setDragHandle(handle);
    setDragStart({
      x: e.clientX,
      y: e.clientY,
      rect: { ...cropRect },
    });
  };

  const handleCropPointerMove = (e: React.PointerEvent): void => {
    if (!dragHandle || !dragStart) return;
    e.preventDefault();

    const { rx, ry } = getRelativePosition(e.clientX, e.clientY);
    const { rect: startRect } = dragStart;

    const newRect = { ...cropRect };

    if (dragHandle === 'move') {
      // Move the entire crop area
      const dxClient = e.clientX - dragStart.x;
      const dyClient = e.clientY - dragStart.y;
      if (!previewRef.current) return;
      const bounds = previewRef.current.getBoundingClientRect();
      const dx = dxClient / bounds.width;
      const dy = dyClient / bounds.height;
      newRect.x = Math.max(0, Math.min(1 - startRect.width, startRect.x + dx));
      newRect.y = Math.max(0, Math.min(1 - startRect.height, startRect.y + dy));
    } else {
      // Resize from handle
      const left = dragHandle.includes('l');
      const right = dragHandle.includes('r');
      const top = dragHandle.includes('t');
      const bottom = dragHandle.includes('b');

      if (left) {
        const newX = Math.min(rx, startRect.x + startRect.width - 0.05);
        newRect.width = startRect.x + startRect.width - newX;
        newRect.x = newX;
      }
      if (right) {
        newRect.width = Math.max(0.05, rx - newRect.x);
      }
      if (top) {
        const newY = Math.min(ry, startRect.y + startRect.height - 0.05);
        newRect.height = startRect.y + startRect.height - newY;
        newRect.y = newY;
      }
      if (bottom) {
        newRect.height = Math.max(0.05, ry - newRect.y);
      }

      // Constrain aspect ratio if set
      if (aspectRatio !== null && imageDimensions) {
        const imgAspect = imageDimensions.width / imageDimensions.height;
        const cropAspect = (newRect.width * imgAspect) / newRect.height;
        const targetAspect = aspectRatio;

        if (cropAspect > targetAspect) {
          // Too wide, reduce width
          newRect.width = (newRect.height * targetAspect) / imgAspect;
        } else {
          // Too tall, reduce height
          newRect.height = (newRect.width * imgAspect) / targetAspect;
        }
      }

      // Clamp to bounds
      newRect.x = Math.max(0, newRect.x);
      newRect.y = Math.max(0, newRect.y);
      newRect.width = Math.min(1 - newRect.x, newRect.width);
      newRect.height = Math.min(1 - newRect.y, newRect.height);
    }

    setCropRect(newRect);
  };

  const handleCropPointerUp = (): void => {
    setDragHandle(null);
    setDragStart(null);
  };

  const hasTransform =
    transform.rotation !== 0 || transform.flipH || transform.flipV || !!transform.cropRect;

  if (!selectedArtwork) {
    return (
      <div className={cn('p-4 text-center text-sm text-muted-foreground', className)}>
        Select an artwork to edit
      </div>
    );
  }

  return (
    <div className={cn('flex flex-col gap-4 p-4 overflow-auto', className)}>
      <h3 className="font-medium text-sm">Image Transform</h3>

      {/* Crop section */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium">Crop</span>
          {!cropActive && (
            <Button variant="outline" size="sm" onClick={startCrop}>
              {transform.cropRect ? 'Edit Crop' : 'Crop'}
            </Button>
          )}
        </div>

        {cropActive && imageUrl && (
          <div className="space-y-2">
            {/* Aspect ratio presets */}
            <div className="flex flex-wrap gap-1">
              {ASPECT_PRESETS.map((preset) => (
                <button
                  key={preset.label}
                  onClick={() => setAspectRatio(preset.value)}
                  className={cn(
                    'px-2 py-1 text-xs rounded border transition-colors',
                    aspectRatio === preset.value
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'border-border hover:bg-muted',
                  )}
                >
                  {preset.label}
                </button>
              ))}
            </div>

            {/* Interactive crop preview */}
            <div
              ref={previewRef}
              className="relative bg-black rounded overflow-hidden select-none touch-none"
              style={{ maxHeight: 240 }}
              onPointerMove={handleCropPointerMove}
              onPointerUp={handleCropPointerUp}
            >
              <img
                src={imageUrl}
                alt="Crop preview"
                className="block w-full"
                style={{ maxHeight: 240, objectFit: 'contain' }}
                draggable={false}
              />

              {/* Dark overlay outside crop */}
              <div className="absolute inset-0 pointer-events-none">
                {/* Top */}
                <div
                  className="absolute left-0 right-0 top-0 bg-black/60"
                  style={{ height: `${cropRect.y * 100}%` }}
                />
                {/* Bottom */}
                <div
                  className="absolute left-0 right-0 bottom-0 bg-black/60"
                  style={{ height: `${(1 - cropRect.y - cropRect.height) * 100}%` }}
                />
                {/* Left */}
                <div
                  className="absolute left-0 bg-black/60"
                  style={{
                    top: `${cropRect.y * 100}%`,
                    height: `${cropRect.height * 100}%`,
                    width: `${cropRect.x * 100}%`,
                  }}
                />
                {/* Right */}
                <div
                  className="absolute right-0 bg-black/60"
                  style={{
                    top: `${cropRect.y * 100}%`,
                    height: `${cropRect.height * 100}%`,
                    width: `${(1 - cropRect.x - cropRect.width) * 100}%`,
                  }}
                />
              </div>

              {/* Crop border */}
              <div
                className="absolute border-2 border-white/80 cursor-move"
                style={{
                  left: `${cropRect.x * 100}%`,
                  top: `${cropRect.y * 100}%`,
                  width: `${cropRect.width * 100}%`,
                  height: `${cropRect.height * 100}%`,
                }}
                onPointerDown={(e) => handleCropPointerDown(e, 'move')}
              >
                {/* Rule of thirds grid */}
                <div className="absolute inset-0 pointer-events-none">
                  <div className="absolute left-1/3 top-0 bottom-0 w-px bg-white/30" />
                  <div className="absolute left-2/3 top-0 bottom-0 w-px bg-white/30" />
                  <div className="absolute top-1/3 left-0 right-0 h-px bg-white/30" />
                  <div className="absolute top-2/3 left-0 right-0 h-px bg-white/30" />
                </div>

                {/* Corner handles */}
                {(['tl', 'tr', 'bl', 'br'] as const).map((corner) => (
                  <div
                    key={corner}
                    className="absolute w-4 h-4 bg-white border border-black/30 rounded-sm"
                    style={{
                      ...(corner.includes('t') ? { top: -8 } : { bottom: -8 }),
                      ...(corner.includes('l') ? { left: -8 } : { right: -8 }),
                      cursor: corner === 'tl' || corner === 'br' ? 'nwse-resize' : 'nesw-resize',
                    }}
                    onPointerDown={(e) => handleCropPointerDown(e, corner)}
                  />
                ))}

                {/* Edge handles */}
                {(['t', 'b', 'l', 'r'] as const).map((edge) => (
                  <div
                    key={edge}
                    className="absolute bg-white border border-black/30 rounded-sm"
                    style={{
                      ...(edge === 't' || edge === 'b'
                        ? {
                            width: 16,
                            height: 6,
                            left: '50%',
                            marginLeft: -8,
                            ...(edge === 't' ? { top: -3 } : { bottom: -3 }),
                            cursor: 'ns-resize',
                          }
                        : {
                            width: 6,
                            height: 16,
                            top: '50%',
                            marginTop: -8,
                            ...(edge === 'l' ? { left: -3 } : { right: -3 }),
                            cursor: 'ew-resize',
                          }),
                    }}
                    onPointerDown={(e) => handleCropPointerDown(e, edge)}
                  />
                ))}
              </div>
            </div>

            {/* Crop actions */}
            <div className="flex gap-2">
              <Button size="sm" onClick={applyCrop} className="flex-1">
                Apply Crop
              </Button>
              <Button variant="outline" size="sm" onClick={resetCrop}>
                Reset
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setCropActive(false)}>
                Cancel
              </Button>
            </div>
          </div>
        )}

        {/* Show current crop info */}
        {!cropActive && transform.cropRect && (
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">
              Cropped: {Math.round(transform.cropRect.width * 100)}% ×{' '}
              {Math.round(transform.cropRect.height * 100)}%
            </span>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 text-xs"
              onClick={() => {
                if (!selectedArtwork) return;
                saveToHistory();
                updateImageTransform(selectedArtwork.id, {
                  cropRect: undefined,
                });
              }}
            >
              Clear
            </Button>
          </div>
        )}
      </div>

      <div className="h-px bg-border" />

      {/* Rotate section */}
      <div className="space-y-2">
        <span className="text-sm font-medium">Rotate</span>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleRotate('ccw')}
            title="Rotate counter-clockwise"
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
              />
            </svg>
            CCW
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleRotate('cw')}
            title="Rotate clockwise"
          >
            CW
            <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
              />
            </svg>
          </Button>
          <span className="text-sm text-muted-foreground ml-2">{transform.rotation}°</span>
        </div>
      </div>

      <div className="h-px bg-border" />

      {/* Flip section */}
      <div className="space-y-2">
        <span className="text-sm font-medium">Flip</span>
        <div className="flex items-center gap-2">
          <Button
            variant={transform.flipH ? 'secondary' : 'outline'}
            size="sm"
            onClick={handleFlipH}
            title="Flip horizontal"
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4"
              />
            </svg>
            Horizontal
          </Button>
          <Button
            variant={transform.flipV ? 'secondary' : 'outline'}
            size="sm"
            onClick={handleFlipV}
            title="Flip vertical"
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M16 17H4m0 0l4-4m-4 4l4 4m0-12h12m0 0l-4-4m4 4l-4 4"
              />
            </svg>
            Vertical
          </Button>
        </div>
      </div>

      {/* Reset all */}
      {hasTransform && (
        <>
          <div className="h-px bg-border" />
          <Button variant="ghost" size="sm" onClick={handleResetAll} className="text-destructive">
            Reset All Transforms
          </Button>
        </>
      )}
    </div>
  );
}
